import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CounselorNav } from '../../counselor-nav/counselor-nav';
import { HasPermissionDirective } from '../../../../../security/has-permission.directive';
import { RoleService, Role, Permission } from '../../../../../services/role.service';
import { USER_PERMISSIONS } from '../../../../../constants/permissions';
import { forkJoin, take } from 'rxjs';

type FormMode = 'closed' | 'create' | 'edit';

@Component({
  selector: 'app-manage-roles',
  standalone: true,
  imports: [CounselorNav, CommonModule, FormsModule, HasPermissionDirective],
  templateUrl: './manage-roles.html',
  styleUrl: './manage-roles.css',
})
export class ManageRoles implements OnInit {
  private roleService = inject(RoleService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  readonly perms = USER_PERMISSIONS;

  roles: Role[] = [];
  permissions: Permission[] = [];
  loading = true;
  errorMessage: string | null = null;
  formMode: FormMode = 'closed';
  editingRoleId: number | null = null;
  roleName = '';
  roleDescription = '';
  selectedPermissionIds = new Set<number>();
  saving = false;
  formError: string | null = null;
  permissionSearch = '';
  permissionCategoryFilter = '';
  confirmingDeleteId: number | null = null;
  deletingId: number | null = null;

  ngOnInit(): void {
    this.loadData();
  }

  private loadData(): void {
    this.loading = true;

    forkJoin({
      roles: this.roleService.getRoles().pipe(take(1)),
      permissions: this.roleService.getPermissions().pipe(take(1)),
    }).subscribe({
      next: ({ roles, permissions }) => {
        this.roles = roles;
        this.permissions = permissions;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to load roles:', err);
        this.errorMessage = err?.error?.message || err?.message || 'Failed to load roles.';
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  getPermissionCount(role: Role): number {
    return role.Permissions?.length ?? 0;
  }

  // ── Form open/close ──────────────────────────────

  startCreate(): void {
    this.formMode = 'create';
    this.editingRoleId = null;
    this.roleName = '';
    this.roleDescription = '';
    this.selectedPermissionIds = new Set<number>();
    this.permissionSearch = '';
    this.permissionCategoryFilter = '';
    this.formError = null;
    this.confirmingDeleteId = null;
  }

  startEdit(role: Role): void {
    this.formMode = 'edit';
    this.editingRoleId = role.id;
    this.roleName = role.name;
    this.roleDescription = role.description ?? '';
    this.selectedPermissionIds = new Set((role.Permissions ?? []).map((p) => p.id));
    this.permissionSearch = '';
    this.permissionCategoryFilter = '';
    this.formError = null;
    this.confirmingDeleteId = null;
  }

  closeForm(): void {
    this.formMode = 'closed';
    this.editingRoleId = null;
    this.formError = null;
  }

  get permissionCategories(): string[] {
    const cats = new Set(this.permissions.map((p) => p.category));
    return Array.from(cats).sort();
  }

  get filteredPermissions(): Permission[] {
    let result = this.permissions;

    if (this.permissionCategoryFilter) {
      result = result.filter((p) => p.category === this.permissionCategoryFilter);
    }

    if (this.permissionSearch.trim()) {
      const term = this.permissionSearch.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          p.key.toLowerCase().includes(term) ||
          (p.description && p.description.toLowerCase().includes(term)),
      );
    }

    return result;
  }

  /** The server only lets users add or remove permissions they hold themselves. */
  isPermissionLocked(permission: Permission): boolean {
    return permission.assignable === false;
  }

  togglePermission(permissionId: number): void {
    const permission = this.permissions.find((p) => p.id === permissionId);
    if (permission && this.isPermissionLocked(permission)) return;

    if (this.selectedPermissionIds.has(permissionId)) {
      this.selectedPermissionIds.delete(permissionId);
    } else {
      this.selectedPermissionIds.add(permissionId);
    }
  }

  hasPermissionSelected(permissionId: number): boolean {
    return this.selectedPermissionIds.has(permissionId);
  }

  get canSave(): boolean {
    return this.roleName.trim().length > 0 && !this.saving;
  }

  save(): void {
    const name = this.roleName.trim();
    if (!name || this.saving) return;

    this.saving = true;
    this.formError = null;

    const description = this.roleDescription.trim() || null;
    const permissionIds = Array.from(this.selectedPermissionIds);

    const request$ =
      this.formMode === 'edit' && this.editingRoleId != null
        ? this.roleService.updateRole(this.editingRoleId, name, description, permissionIds)
        : this.roleService.createRole(name, description, permissionIds);

    request$.pipe(take(1)).subscribe({
      next: () => {
        this.saving = false;
        this.closeForm();
        this.loadData();
      },
      error: (err) => {
        console.error('Failed to save role:', err);
        this.formError = err?.error?.message || err?.message || 'Failed to save role.';
        this.saving = false;
        this.cdr.detectChanges();
      },
    });
  }

  requestDelete(roleId: number): void {
    this.confirmingDeleteId = roleId;
  }

  cancelDelete(): void {
    this.confirmingDeleteId = null;
  }

  confirmDelete(role: Role): void {
    if (this.deletingId != null) return;

    this.deletingId = role.id;
    this.errorMessage = null;

    this.roleService
      .deleteRole(role.id)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.deletingId = null;
          this.confirmingDeleteId = null;
          if (this.editingRoleId === role.id) {
            this.closeForm();
          }
          this.loadData();
        },
        error: (err) => {
          console.error('Failed to delete role:', err);
          this.errorMessage = err?.error?.message || err?.message || 'Failed to delete role.';
          this.deletingId = null;
          this.confirmingDeleteId = null;
          this.cdr.detectChanges();
        },
      });
  }

  back(): void {
    this.router.navigate(['/counselors/users']);
  }
}
